import React from 'react'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import Input from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import { observer } from 'mobx-react-lite'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'
import InputProps from 'reusable/mobx-store-utils/form-handlers/InputProps'

export const CustomTableToolbar = observer(function CustomTableToolbar<RowData extends AnyObject, RowStore>() {
   useCustomTableRenderLog('CustomTableToolbar')
   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()

   const localStore = useStore(() => ({
      get toolbarContainerStyle(): React.CSSProperties {
         return {
            position: 'sticky',
            top: '0px',
            zIndex: 1,
         }
      },
      get boxStyle(): React.CSSProperties {
         return { marginLeft: 'auto', display: 'flex', alignItems: 'center' }
      },
      get progressButtonStyle(): React.CSSProperties {
         return {
            visibility: customTableStore.isLoading && !customTableStore.hasRefreshButton ? 'visible' : 'hidden',
         }
      },
      get progressTooltipStyle(): React.CSSProperties {
         return localStore.progressButtonStyle
      },
      get loadingTitle() {
         return customTableStore.isLoading && !customTableStore.hasRefreshButton ? 'Loading...' : ''
      },
      get searchInputProps() {
         return { style: customTableStore.sp.styling?.searchFieldInputStyle }
      },
      get toolbarTitle() {
         const title = customTableStore.sp.title
         return typeof title === 'string' ? <Typography variant="h6">{title}</Typography> : title
      },
   }))

   const styling = customTableStore.sp.styling || {}
   const ToolbarOverride = customTableStore.sp.overrides?.toolbar

   return (
      <div ref={customTableStore.toolbarDivRef} style={localStore.toolbarContainerStyle}>
         {ToolbarOverride ? (
            <ToolbarOverride store={customTableStore} />
         ) : (
            <Toolbar>
               {localStore.toolbarTitle}
               <Box style={localStore.boxStyle}>
                  <Tooltip title={localStore.loadingTitle} style={localStore.progressTooltipStyle}>
                     <IconButton style={localStore.progressButtonStyle} size="large">
                        <CircularProgress
                           disableShrink
                           size={24}
                           style={localStore.progressButtonStyle}
                        />
                     </IconButton>
                  </Tooltip>

                  {customTableStore.searchable && (
                     <InputProps
                        stateObject={customTableStore}
                        propertyName="searchString"
                        onValueChanged={customTableStore.onSearchChanged}>
                        <Input
                           style={styling.searchFieldStyle}
                           inputProps={localStore.searchInputProps}
                           startAdornment={
                              <InputAdornment position="start">
                                 <SearchIcon />
                              </InputAdornment>
                           }
                           endAdornment={
                              <InputAdornment position="end">
                                 <IconButton
                                    disabled={!customTableStore.searchString}
                                    aria-label="clear"
                                    onClick={customTableStore.clearSearchString}
                                    size="large">
                                    <ClearIcon />
                                 </IconButton>
                              </InputAdornment>
                           }
                        />
                     </InputProps>
                  )}
                  {customTableStore.fullscreenable && (
                     <Tooltip title="Toggle fullscreen">
                        <IconButton onClick={customTableStore.toggleFullscreenByUser} size="large">
                           {customTableStore.fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                     </Tooltip>
                  )}
                  {customTableStore.sp.userCanToggleSize && (
                     <Tooltip title="Toggle table density">
                        <IconButton onClick={customTableStore.toggleSize} size="large">
                           {customTableStore.size === 'small' ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                        </IconButton>
                     </Tooltip>
                  )}
                  {customTableStore.buttons}
               </Box>
            </Toolbar>
         )}
         {customTableStore.sp.renderBetweenToolbarAndTable}
      </div>
   )
})
